import { Component, EventEmitter, Inject, inject, OnDestroy, OnInit, Optional, Output } from '@angular/core';

import moment from 'moment';
import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { Subscription } from 'rxjs';
import {
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TeamAdminApiService } from '@services/team-admin-api.service';
import { ToastrService } from '@services/toaster.service';
import { TasksService } from '@services/tasks.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ButtonComponent } from '@mds/ui';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { TextEditorComponent } from '@components/text-editor/text-editor.component';
import { MatInput, MatPrefix, MatSuffix } from '@angular/material/input';

@Component({
  selector: 'app-inquiry-dialog',
  standalone: true,
  imports: [
    ButtonComponent,
    MatDialogModule,
    MdsIconsModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    TextEditorComponent,
    MatInput,
    MatPrefix,
    MatSuffix
],
  templateUrl: './inquiry-dialog.component.html',
  styleUrls: ['./inquiry-dialog.component.scss'],
})
export class InquiryDialogComponent implements OnInit, OnDestroy {
  icons = MdsIcon;
  selectedUser: any[] = [];
  assingee: any[] = [];
  optionsList: any[] = [];
  separatorKeysCodes: number[] = [ENTER, COMMA, SPACE];
  editorNote: any;
  subscribers: Subscription[] = [];
  removable = true;

  userControl = new UntypedFormControl(null, Validators.required);
  isEditMode = false;
  @Output() handleBtnEvent = new EventEmitter<{
    actionName: string;
    data?: { editedTask: any; userList: any[] };
  }>();
  finalData: any;
  todoForm: FormGroup;
  private teamAdminApiService = inject(TeamAdminApiService);
  private toastService = inject(ToastrService);
  private tasksService = inject(TasksService);

  constructor(
    @Optional() private dialogRef: MatDialogRef<InquiryDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.isEditMode = data.isEditMode;

    this.finalData = this.isEditMode ? data.extraData?.selectedTask : data;
    this.selectedUser = this.isEditMode ? data.extraData.selectedUser : [];

    if (this.isEditMode) {
      this.finalData.title = data.title;
      this.finalData.submit_button_text = data.submit_button_text;
    }
  }

  ngOnInit(): void {
    if (this.isEditMode) {
      this.todoForm = new UntypedFormGroup({
        name: new UntypedFormControl(this.finalData?.name, Validators.required),
        due_date: new UntypedFormControl(this.finalData.due_date),
        start_date: new UntypedFormControl(this.finalData.start_date),
        note: new UntypedFormControl(this.finalData.note),
      });

      this.editorNote = {
        note: this.finalData.note,
      };
    } else {
      this.todoForm = new UntypedFormGroup({
        name: new UntypedFormControl(null, Validators.required),
        due_date: new UntypedFormControl(new Date()),
        start_date: new UntypedFormControl(new Date()),
        note: new UntypedFormControl(),
      });

      this.editorNote = {
        note: '',
      };
    }

    this.subscribers.push(
      this.userControl.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe((value) => {
          if (!value) return;
          this.optionsList = [];
          this.fetchData(value);
        })
    );
  }

  ngOnDestroy(): void {
    this.subscribers.forEach((sub) => {
      sub?.unsubscribe();
    });
  }

  fetchData(searchKey: string) {
    this.optionsList = [];
    this.teamAdminApiService.getTeamAndUser(searchKey).subscribe((res) => {
      if (Array.isArray(res)) {
        this.optionsList = res;
      }
    });
  }

  remove(name): void {
    this.selectedUser = this.selectedUser.filter((user) => user !== name);
    this.assingee = this.assingee.filter(
      (user) =>
        !(user.name === name || `${user.firstname} ${user.lastname}` === name)
    );
  }

  validateEmail(email) {
    const regularExpression =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regularExpression.test(String(email).toLowerCase());
  }

  add(input) {
    const value = input.value.trim();

    if (value && this.validateEmail(value)) {
      this.selectedUser.push(value);
    }
    input.value = '';
  }

  updateSelection(opt, input) {
    const itemAlreadyExist = this.assingee.find(
      (item) => item.rid === opt.value.rid
    );
    if (!itemAlreadyExist) {
      if (opt.value.type === 'user') {
        this.selectedUser.push(`${opt.value.firstname} ${opt.value.lastname}`);
      } else {
        this.selectedUser.push(opt.value.name);
      }
      this.assingee.push(opt.value);
    }

    this.userControl.setValue('');
    input.value = '';
  }

  submitData() {
    if (this.todoForm.status === 'INVALID' || this.selectedUser.length === 0) {
      return;
    }

    const toEmail = this.selectedUser.filter((e) => this.validateEmail(e));
    const toUser = this.assingee.map((e) => e.rid);
    const details = this.todoForm.value;
    const task = {
      rid: this.finalData.rid ? this.finalData.rid : '',
      name: details.name,
      note: this.editorNote.note,
      start_date: moment(details.start_date).format('YYYY-MM-DD HH:mm:ss'),
      due_date: moment(details.due_date).format('YYYY-MM-DD HH:mm:ss'),
      completed: false,
      starred: false,
      important: false,
      deleted: false,
      tags: [],
      assigned_to: toUser,
      to_email: toEmail,
    };
    this.tasksService.addTask(task).subscribe(() => {
      this.toastService.show('Task created successfully', 3000);
      this.dialogRef.close({
        actionName: 'refreshList',
      });
    });
  }

  close() {
    this.dialogRef.close();
  }

  cancelEdit() {
    this.dialogRef.close({
      actionName: 'cancelEdit',
    });
  }

  saveEdit() {
    this.dialogRef.close({
      actionName: 'saveEdit',
      data: { editedTask: this.finalData, userList: this.selectedUser },
    });
  }
}
